// Copia de sólo lectura del catálogo y los defaults por rol del backend
// (functions/_app/lib/permisos.ts), usada acá únicamente para decidir qué
// mostrar en la interfaz (menú, botones, etiquetas "heredado/adicional/
// denegado"). La autorización real es 100% del backend — ocultar un botón
// acá nunca reemplaza el requierePermiso() del lado del servidor.
import type { Permiso, Rol } from "../../shared/types";

export const CATALOGO_PERMISOS: Permiso[] = [
  "usuarios.ver",
  "usuarios.crear",
  "usuarios.editar",
  "usuarios.desactivar",
  "usuarios.reactivar",
  "usuarios.restablecer_password",
  "usuarios.cambiar_rol",
  "usuarios.ver_actividad",
  "roles.ver",
  "roles.administrar",
  "permisos.ver",
  "permisos.administrar",
  "clientes.ver",
  "clientes.crear",
  "clientes.editar",
  "clientes.desactivar",
  "expedientes.ver_todos",
  "expedientes.ver_asignados",
  "expedientes.crear",
  "expedientes.editar",
  "expedientes.archivar",
  "expedientes.reasignar",
  "expedientes.ver_evaluacion",
  "expedientes.editar_evaluacion",
  "catalogos_estudio.ver",
  "catalogos_estudio.administrar",
  "actuaciones.ver",
  "actuaciones.crear",
  "actuaciones.editar",
  "actuaciones.eliminar",
  "documentos.ver",
  "documentos.subir",
  "documentos.descargar",
  "documentos.eliminar",
  "agenda.ver",
  "agenda.crear",
  "agenda.editar",
  "agenda.eliminar",
  "economia.ver",
  "economia.crear",
  "economia.editar",
  "economia.anular",
  "economia.administrar_catalogos",
  "economia.autorizar_descuento_extraordinario",
  "economia.configurar_jus",
  "auditoria.ver",
  "configuracion.ver",
  "configuracion.editar",
  "procesos.ver",
  "procesos.administrar",
  "procesos.mover_etapa",
];

export const PERMISOS_POR_ROL: Record<Rol, Permiso[]> = {
  ADMINISTRADOR: [...CATALOGO_PERMISOS],
  ABOGADO: [
    "expedientes.ver_asignados",
    "expedientes.editar",
    "expedientes.ver_evaluacion",
    "expedientes.editar_evaluacion",
    "catalogos_estudio.ver",
    "actuaciones.ver",
    "actuaciones.crear",
    "actuaciones.editar",
    "documentos.ver",
    "documentos.subir",
    "documentos.descargar",
    "agenda.ver",
    "agenda.crear",
    "agenda.editar",
    "clientes.ver",
    "procesos.ver",
    "procesos.mover_etapa",
  ],
  PROCURADOR: [
    "expedientes.ver_asignados",
    "actuaciones.ver",
    "actuaciones.crear",
    "documentos.ver",
    "documentos.subir",
    "agenda.ver",
    "agenda.crear",
    "agenda.editar",
    "clientes.ver",
  ],
  ADMINISTRATIVO: [
    "clientes.ver",
    "clientes.crear",
    "clientes.editar",
    "agenda.ver",
    "agenda.crear",
    "agenda.editar",
    "documentos.ver",
    "documentos.subir",
    "expedientes.ver_asignados",
  ],
  RECEPCION: ["clientes.ver", "clientes.crear", "agenda.ver"],
};

export const GRUPOS_PERMISOS: { grupo: string; etiqueta: string }[] = [
  { grupo: "usuarios", etiqueta: "Usuarios" },
  { grupo: "roles", etiqueta: "Roles" },
  { grupo: "permisos", etiqueta: "Permisos" },
  { grupo: "clientes", etiqueta: "Clientes" },
  { grupo: "expedientes", etiqueta: "Expedientes" },
  { grupo: "catalogos_estudio", etiqueta: "Catálogos del estudio" },
  { grupo: "actuaciones", etiqueta: "Actuaciones" },
  { grupo: "documentos", etiqueta: "Documentos" },
  { grupo: "agenda", etiqueta: "Agenda" },
  { grupo: "economia", etiqueta: "Economía" },
  { grupo: "auditoria", etiqueta: "Auditoría" },
  { grupo: "configuracion", etiqueta: "Configuración" },
  { grupo: "procesos", etiqueta: "Procesos de Trabajo" },
];

export const ETIQUETAS_PERMISOS: Record<Permiso, string> = {
  "usuarios.ver": "Ver usuarios",
  "usuarios.crear": "Crear usuarios",
  "usuarios.editar": "Editar usuarios",
  "usuarios.desactivar": "Desactivar usuarios",
  "usuarios.reactivar": "Reactivar usuarios",
  "usuarios.restablecer_password": "Restablecer contraseña",
  "usuarios.cambiar_rol": "Cambiar rol",
  "usuarios.ver_actividad": "Ver último acceso/actividad",
  "roles.ver": "Ver roles",
  "roles.administrar": "Administrar roles",
  "permisos.ver": "Ver permisos",
  "permisos.administrar": "Administrar permisos",
  "clientes.ver": "Ver clientes",
  "clientes.crear": "Crear clientes",
  "clientes.editar": "Editar clientes",
  "clientes.desactivar": "Desactivar clientes",
  "expedientes.ver_todos": "Ver todos los expedientes del estudio",
  "expedientes.ver_asignados": "Ver expedientes asignados",
  "expedientes.crear": "Crear expedientes",
  "expedientes.editar": "Editar expedientes",
  "expedientes.archivar": "Archivar expedientes",
  "expedientes.reasignar": "Reasignar responsable",
  "expedientes.ver_evaluacion": "Ver evaluación y estrategia del expediente",
  "expedientes.editar_evaluacion": "Editar evaluación y estrategia del expediente",
  "catalogos_estudio.ver": "Ver catálogos del estudio (estados procesales, valores estratégicos)",
  "catalogos_estudio.administrar": "Administrar catálogos del estudio",
  "actuaciones.ver": "Ver actuaciones",
  "actuaciones.crear": "Crear actuaciones",
  "actuaciones.editar": "Editar actuaciones",
  "actuaciones.eliminar": "Eliminar actuaciones",
  "documentos.ver": "Ver documentos",
  "documentos.subir": "Subir documentos",
  "documentos.descargar": "Descargar documentos",
  "documentos.eliminar": "Eliminar documentos",
  "agenda.ver": "Ver agenda",
  "agenda.crear": "Crear eventos de agenda",
  "agenda.editar": "Editar eventos de agenda",
  "agenda.eliminar": "Eliminar eventos de agenda",
  "economia.ver": "Ver economía",
  "economia.crear": "Crear movimientos económicos",
  "economia.editar": "Editar movimientos económicos",
  "economia.anular": "Anular movimientos económicos",
  "economia.administrar_catalogos": "Administrar categorías económicas",
  "economia.autorizar_descuento_extraordinario": "Autorizar descuentos extraordinarios en honorarios",
  "economia.configurar_jus": "Configurar valor JUS vigente (Ley 9.131)",
  "auditoria.ver": "Ver auditoría",
  "configuracion.ver": "Ver configuración del estudio",
  "configuracion.editar": "Editar configuración del estudio",
  "procesos.ver": "Ver Procesos de Trabajo (Kanban, timeline del expediente)",
  "procesos.administrar": "Configurar Procesos de Trabajo (etapas, checklist, automatizaciones)",
  "procesos.mover_etapa": "Mover expedientes de etapa",
};

export function permisosEfectivos(
  rol: Rol,
  overrides: { permiso: Permiso; concedido: boolean }[],
): Set<Permiso> {
  const efectivos = new Set<Permiso>(PERMISOS_POR_ROL[rol]);
  for (const o of overrides) {
    if (o.concedido) efectivos.add(o.permiso);
    else efectivos.delete(o.permiso);
  }
  return efectivos;
}
